import { useState } from "react";
import { usePendingRelease, type HeldGroup } from "./hooks/usePendingRelease";
import { timeAgo } from "@/features/queues/lib/timeAgo";
import { toast } from "@/components/Toaster";
import styles from "./PendingReleasePage.module.css";

// ── Row ──────────────────────────────────────────────────────────────────────

interface RowProps {
  group: HeldGroup;
  onRelease: (id: string) => Promise<void>;
  busy: boolean;
}

function GroupRow({ group, onRelease, busy }: RowProps) {
  // Single derivation: reason drives the blocked state — avoids parallel booleans.
  const blockReason: string | null = group.standingHardBalance
    ? "Out of balance — resolve before release"
    : !group.destinationActive
      ? "Destination is inactive"
      : null;
  const blocked = blockReason !== null;

  return (
    <tr>
      <td>
        <span className={styles.destination}>{group.destinationName}</span>
      </td>
      <td>
        <span className={styles.jobType}>{group.jobTypeName}</span>
      </td>
      <td>
        <span className={styles.docCount}>{group.docCount}</span>
      </td>
      <td>
        <span className={styles.trigger}>{group.trigger}</span>
      </td>
      <td>
        <span className={styles.batchLabel}>{group.batchLabel ?? "—"}</span>
      </td>
      <td>
        <span className={styles.age}>{timeAgo(group.createdAt)}</span>
      </td>
      <td>
        <div className={styles.actionCell}>
          <button
            type="button"
            className={blocked ? styles.btn : `${styles.btn} ${styles.btnPrimary}`}
            disabled={blocked || busy}
            aria-label={`Release group to ${group.destinationName}`}
            onClick={() => void onRelease(group.id)}
          >
            Release
          </button>
          {blockReason && (
            <span className={styles.blockReason}>{blockReason}</span>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function PendingReleasePage() {
  const { groups, loading, error, release } = usePendingRelease();
  const [busyId, setBusyId] = useState<string | null>(null);

  async function handleRelease(id: string) {
    setBusyId(id);
    try {
      await release(id);
      toast.success("Bundle released successfully.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Release failed.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Pending Release</h1>
      <p className={styles.subhead}>
        Export bundles held for manual operator release. Review and release when
        ready.
      </p>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {loading && <p className={styles.loading}>Loading…</p>}

      {!loading && groups.length === 0 && !error && (
        <div className={styles.emptyState}>
          <p className={styles.emptyStateTitle}>No bundles awaiting release</p>
          <p className={styles.emptyStateBody}>
            Export groups held for manual release will appear here.
          </p>
        </div>
      )}

      {!loading && groups.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Destination</th>
              <th scope="col">Job Type</th>
              <th scope="col"># Docs</th>
              <th scope="col">Trigger</th>
              <th scope="col">Batch</th>
              <th scope="col">Age</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <GroupRow
                key={g.id}
                group={g}
                onRelease={handleRelease}
                busy={busyId === g.id}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
